import { privateClient } from "@/api/privateClient";
import { INSTITUTION_ADMIN_ENDPOINTS } from "../../../institutionAdmin.endpoints";
import type { TripSummary, RouteStop, ActiveTrip, BoardingsDay, TransportRoute, TransportAssignment } from "../types";

const TRANSPORT = INSTITUTION_ADMIN_ENDPOINTS.FACILITIES.TRANSPORT;

export async function fetchRoutes(institutionId: number): Promise<TransportRoute[]> {
  const { data } = await privateClient.get(TRANSPORT.ROUTES(institutionId));
  return data.routes ?? [];
}

export async function fetchBoardings(institutionId: number, date?: string): Promise<BoardingsDay> {
  const { data } = await privateClient.get(TRANSPORT.BOARDINGS(institutionId), { params: date ? { date } : undefined });
  return data;
}

export async function fetchTrips(institutionId: number, date?: string): Promise<{ date: string; trips: TripSummary[] }> {
  const { data } = await privateClient.get(TRANSPORT.TRIPS(institutionId), { params: date ? { date } : undefined });
  return data;
}

export async function fetchActiveTrips(institutionId: number): Promise<ActiveTrip[]> {
  const { data } = await privateClient.get(TRANSPORT.LIVE(institutionId));
  return data.trips ?? [];
}

export interface RoutePayload {
  routeName: string;
  startPoint?: string;
  returnPoint?: string;
  vehicleNumber?: string;
  /** A real DRIVER on this institution's staff — preferred over driverName/driverPhone. */
  driverStaffId?: number | null;
  driverName?: string;
  driverPhone?: string;
  capacity?: number;
}

export async function createRoute(institutionId: number, payload: RoutePayload): Promise<TransportRoute> {
  const { data } = await privateClient.post(TRANSPORT.ROUTES(institutionId), payload);
  return data.route;
}

export async function updateRoute(
  institutionId: number,
  routeId: number,
  payload: Partial<RoutePayload> & { isActive?: boolean }
): Promise<TransportRoute> {
  const { data } = await privateClient.patch(TRANSPORT.ROUTE(institutionId, routeId), payload);
  return data.route;
}

export async function saveRouteStops(institutionId: number, routeId: number, stops: RouteStop[]): Promise<RouteStop[]> {
  const { data } = await privateClient.put(TRANSPORT.STOPS(institutionId, routeId), { stops });
  return data.stops;
}

export async function setAssignmentStop(institutionId: number, assignmentId: number, change: { stopId?: number | null; pickupPoint?: string }): Promise<void> {
  await privateClient.put(TRANSPORT.ASSIGNMENT_STOP(institutionId, assignmentId), change);
}

export async function deleteRoute(institutionId: number, routeId: number): Promise<void> {
  await privateClient.delete(TRANSPORT.ROUTE(institutionId, routeId));
}

/** Every route currently assigned to one driver (institution_staff.id) — usually zero or one, but not enforced as exactly one. */
export async function fetchDriverRoutes(institutionId: number, driverStaffId: number): Promise<TransportRoute[]> {
  const { data } = await privateClient.get(TRANSPORT.DRIVER_ROUTES(institutionId, driverStaffId));
  return data.routes ?? [];
}

export async function assignStudent(
  institutionId: number,
  payload: { routeId: number; studentId: number; pickupPoint?: string; stopId?: number; academicYearId?: number }
): Promise<TransportAssignment> {
  const { data } = await privateClient.post(TRANSPORT.ASSIGN(institutionId), payload);
  return data.assignment;
}

export async function carryForwardAssignments(institutionId: number): Promise<{ carried: number; closed: number; skipped: number }> {
  const { data } = await privateClient.post(TRANSPORT.CARRY_FORWARD(institutionId));
  return data;
}

export async function voidBoarding(institutionId: number, boardingId: number, reason: string): Promise<void> {
  await privateClient.post(TRANSPORT.VOID_BOARDING(institutionId, boardingId), { reason });
}

export async function endAssignment(institutionId: number, assignmentId: number): Promise<TransportAssignment> {
  const { data } = await privateClient.post(TRANSPORT.END(institutionId, assignmentId));
  return data.assignment;
}

export async function fetchStudentAssignments(
  institutionId: number,
  studentId: number,
  academicYearId?: number
): Promise<TransportAssignment[]> {
  const { data } = await privateClient.get(TRANSPORT.STUDENT_ASSIGNMENT(institutionId, studentId), { params: { academicYearId } });
  return data.assignments ?? [];
}
